import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { getSwalTheme, getSwalConfirmTheme } from '../utils/swalTheme';
import {
  DashboardHeader,
  EmptyState,
  LoadingState,
  Pagination
} from '../components/dashboard';
import PostListItem from '../components/PostListItem';
import AdCard from '../components/AdCard';
import EditShiftModal from '../components/EditShiftModal';
import EditDriverPostModal from '../components/EditDriverPostModal';
import ShiftDetailsModal from '../components/ShiftDetailsModal';
import DriverPostProfileModal from '../components/DriverPostProfileModal';
import api from '../services/api';
import { PAGINATION } from '../constants/limits';
import './DashboardPage.css';

const DashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editModalShift, setEditModalShift] = useState(null);
  const [editModalDriverPost, setEditModalDriverPost] = useState(null);
  const [detailsModalShift, setDetailsModalShift] = useState(null);
  const [driverProfilePost, setDriverProfilePost] = useState(null);
  const [feedAds, setFeedAds] = useState([]);
  const { user, isUser } = useAuth();

  // Read filters from URL params
  const filtersFromURL = useCallback(() => ({
    post_type: searchParams.get('post_type') || '',
    vehicle_type: searchParams.get('vehicle_type') || '',
    my_posts: searchParams.get('my_posts') === 'true'
  }), [searchParams]);

  const pageFromURL = useCallback(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  }, [searchParams]);

  const [filters, setFilters] = useState(filtersFromURL);
  const [currentPage, setCurrentPage] = useState(pageFromURL);
  const [paginationInfo, setPaginationInfo] = useState(null);

  // Sync filters from URL when searchParams change (e.g. navbar "My Posts" link)
  useEffect(() => {
    setFilters(filtersFromURL());
    setCurrentPage(pageFromURL());
  }, [filtersFromURL, pageFromURL]);

  // Write filter state back to URL (one-way: state → URL, no feedback loop)
  const updateURLParams = useCallback((newFilters, newPage) => {
    const params = new URLSearchParams();
    if (newFilters.post_type) params.set('post_type', newFilters.post_type);
    if (newFilters.vehicle_type) params.set('vehicle_type', newFilters.vehicle_type);
    if (newFilters.my_posts) params.set('my_posts', 'true');
    if (newPage > 1) params.set('page', newPage.toString());
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Listen for shift details modal from notification click
  useEffect(() => {
    const handleOpenShiftDetails = (event) => {
      setDetailsModalShift(event.detail);
    };
    window.addEventListener('openShiftDetails', handleOpenShiftDetails);
    return () => window.removeEventListener('openShiftDetails', handleOpenShiftDetails);
  }, []);

  useEffect(() => {
    fetchAllPosts();
  }, [filters, currentPage]);

  // Fetch ads once on mount
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await api.get('/ads?page_type=dashboard');
        setFeedAds(response.data.ads || []);
      } catch (err) {
        // Ads are non-critical, fail silently
      }
    };
    fetchAds();
  }, []);


  const fetchAllPosts = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();

      if (filters.post_type) params.append('post_type', filters.post_type);
      if (filters.vehicle_type) params.append('vehicle_type', filters.vehicle_type);
      if (filters.my_posts) {
        params.append('my_posts', 'true');
        params.append('include_expired', 'true');
      }

      params.append('page', currentPage.toString());
      params.append('limit', PAGINATION.DEFAULT_LIMIT.toString());

      const response = await api.get(`/posts?${params.toString()}`);
      setPosts(response.data.posts || []);
      setPaginationInfo(response.data.pagination);
    } catch (err) {
      setError('Failed to load posts');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      updateURLParams(newFilters, 1);
      return newFilters;
    });
  };

  const handleViewDetails = (shift) => setDetailsModalShift(shift);
  const handleViewDriverProfile = (post) => setDriverProfilePost(post);

  const handleEdit = (post) => {
    if (post.post_type === 'driver') {
      setEditModalDriverPost(post);
    } else {
      setEditModalShift(post);
    }
  };

  const handleRenew = async (postId, postType) => {
    const isOwnerPost = postType === 'owner' || postType === 'shift';
    try {
      if (isOwnerPost) {
        await api.post(`/shifts/${postId}/renew`);
      } else {
        await api.post(`/driver-posts/${postId}/renew`);
      }
      fetchAllPosts();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Renewed!',
        text: 'Your post has been renewed for 30 more days.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Renew Failed',
        text: err.response?.data?.error || 'Failed to renew post.'
      });
    }
  };

  const handleDelete = async (postId, postType) => {
    const isOwnerPost = postType === 'owner' || postType === 'shift';
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: isOwnerPost ? 'Delete Car Listing?' : 'Delete Driver Listing?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete it'
    });

    if (!result.isConfirmed) return;

    try {
      if (isOwnerPost) {
        await api.delete(`/shifts/${postId}`);
      } else {
        await api.delete(`/driver-posts/${postId}`);
      }
      fetchAllPosts();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Deleted!',
        text: 'Your post has been deleted.'
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Delete Failed',
        text: err.response?.data?.error || 'Failed to delete.'
      });
    }
  };

  const refreshData = () => {
    fetchAllPosts();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateURLParams(filters, newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="dashboard-page">
      <DashboardHeader
        isUser={isUser}
        myPosts={filters.my_posts}
        onToggleMyPosts={() => handleFilterChange('my_posts', !filters.my_posts)}
      />

      <div className="dashboard-layout">
        {/* Sidebar Filters */}
        <aside className="dashboard-sidebar">
          <div className="filter-section">
            <h3 className="filter-title">Post Type</h3>
            <div className="filter-options">
              <label className={`filter-option ${filters.post_type === '' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="post_type"
                  value=""
                  checked={filters.post_type === ''}
                  onChange={(e) => handleFilterChange('post_type', e.target.value)}
                />
                <span className="filter-label">All Listings</span>
              </label>
              <label className={`filter-option ${filters.post_type === 'driver' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="post_type"
                  value="driver"
                  checked={filters.post_type === 'driver'}
                  onChange={(e) => handleFilterChange('post_type', e.target.value)}
                />
                <span className="filter-label">Driver Listings</span>
              </label>
              <label className={`filter-option ${filters.post_type === 'owner' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="post_type"
                  value="owner"
                  checked={filters.post_type === 'owner'}
                  onChange={(e) => handleFilterChange('post_type', e.target.value)}
                />
                <span className="filter-label">Car Listings</span>
              </label>
            </div>
          </div>

          <div className="filter-section">
            <h3 className="filter-title">Work Type</h3>
            <div className="filter-options">
              <label className={`filter-option ${filters.vehicle_type === '' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="vehicle_type"
                  value=""
                  checked={filters.vehicle_type === ''}
                  onChange={(e) => handleFilterChange('vehicle_type', e.target.value)}
                />
                <span className="filter-label">All Types</span>
              </label>
              <label className={`filter-option ${filters.vehicle_type === 'yellow_cab' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="vehicle_type"
                  value="yellow_cab"
                  checked={filters.vehicle_type === 'yellow_cab'}
                  onChange={(e) => handleFilterChange('vehicle_type', e.target.value)}
                />
                <span className="filter-label yellow">Yellow Cab</span>
              </label>
              <label className={`filter-option ${filters.vehicle_type === 'green_boro' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="vehicle_type"
                  value="green_boro"
                  checked={filters.vehicle_type === 'green_boro'}
                  onChange={(e) => handleFilterChange('vehicle_type', e.target.value)}
                />
                <span className="filter-label green">Green Boro</span>
              </label>
              <label className={`filter-option ${filters.vehicle_type === 'black_car' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="vehicle_type"
                  value="black_car"
                  checked={filters.vehicle_type === 'black_car'}
                  onChange={(e) => handleFilterChange('vehicle_type', e.target.value)}
                />
                <span className="filter-label black">Black Car</span>
              </label>
            </div>
          </div>

        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {error && <div className="dashboard-error">{error}</div>}

          {loading ? (
            <LoadingState />
          ) : posts.length === 0 ? (
            <EmptyState
              activeTab={filters.post_type || 'all'}
              isUser={isUser}
            />
          ) : (
            <div className="posts-list">
              {posts.map((post, index) => {
                const postIndex = index + 1;
                const adForSlot = feedAds.find(ad => ad.display_frequency === postIndex);
                return (
                  <React.Fragment key={`${post.post_type}-${post.id}`}>
                    {adForSlot && <AdCard ad={adForSlot} />}
                    <PostListItem
                      post={post}
                      userRole={user?.role}
                      userId={user?.id}
                      onViewDetails={handleViewDetails}
                      onViewProfile={handleViewDriverProfile}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onRenew={handleRenew}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          )}
          {paginationInfo && paginationInfo.pages > 1 && (
            <Pagination
              pagination={paginationInfo}
              onPageChange={handlePageChange}
            />
          )}
        </main>
      </div>

      {editModalShift && (
        <EditShiftModal
          shift={editModalShift}
          onClose={() => setEditModalShift(null)}
          onUpdate={refreshData}
        />
      )}

      {editModalDriverPost && (
        <EditDriverPostModal
          post={editModalDriverPost}
          onClose={() => setEditModalDriverPost(null)}
          onUpdate={refreshData}
        />
      )}

      {detailsModalShift && (
        <ShiftDetailsModal
          shift={detailsModalShift}
          onClose={() => setDetailsModalShift(null)}
          userRole={user?.role}
        />
      )}

      {driverProfilePost && (
        <DriverPostProfileModal
          post={driverProfilePost}
          onClose={() => setDriverProfilePost(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
